import cloneDeep from 'clone-deep'
import { type EmailValue, EmailTo } from 'components/EmailTo'
import { InputType, RefundReason } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, StripeIntentStatus } from 'services'
import { Modal } from 'stories/components'
import { getPrice2decimal, InputValidate, removeComma } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'
import { RenderInput } from 'utils/RenderInput'

const defaultInputs = (): Record<string, InputType> => {
  return {
    amount: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Refund Amount',
      required: true,
      prefix: '$',
    },
    reason: {
      inputType: 'select',
      title: 'Refund Reason',
      options: RefundReason,
      required: true,
      hasDefaultOption: true,
    },
    description: {
      inputType: 'textarea',
      title: 'Description',
      required: true,
      rows: 3,
    },
  }
}
export const RefundModal = ({
  intentId,
  amount,
  history,
  emailParties = [],
  onOk,
  onClose,
}: {
  intentId: string
  amount: number
  history: Record<string, any>
  emailParties?: { party: string; email: string }[]
  onOk: Function
  onClose: Function
}) => {
  const [inputs, setInputs] = useState(defaultInputs())
  const [remainAmount, setRemainAmount] = useState(0)
  const [emails, setEmails] = useState<EmailValue>({})
  const [templates, setTemplates] = useState<Record<string, any>[]>([])

  useEffect(() => {
    let temp = amount
    history.map((item: any) => {
      if (item.status === StripeIntentStatus.partially_refunded) temp -= item.amount
    })
    setRemainAmount(temp)
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      const data = await getAdminConfig('stripeTemplate')
      setTemplates(Array.isArray(data) ? data : [])
    } catch {
      // non-critical
    }
  }

  const onSelectTemplate = async () => {
    if (!templates.length) {
      toast('No templates available.', { type: 'info' })
      return
    }
    const options = templates.map((t) => ({ name: t.title, value: t }))
    const result: any = await confirmOptions('Select a Template', options)
    if (!result || result === false) return
    const temp = cloneDeep(inputs)
    temp.description.value = result.description
    setInputs(temp)
  }

  const onModalOk = () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}

    Object.keys(inputs).map((key) => {
      newInputs[key].error = InputValidate({ ...newInputs[key], value: newInputs[key].value })
      if (newInputs[key].error?.length) hasError = true
      data[key] = newInputs[key].value
      // A thousandSep input holds the amount as it is displayed ("4,954.00"); the backend reads it
      // with Number and gets NaN, which reaches Stripe as "Invalid integer: NaN".
      if (newInputs[key].type === 'thousandSep') data[key] = removeComma(data[key])
    })
    setInputs(newInputs)
    if (hasError) return
    if (data.amount > remainAmount) {
      toast('Refund amount should not exceed the payment amount.', { type: 'error' })
      return
    }
    onOk(intentId, { ...data, emailTo: emails })
  }
  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(inputs)
    temp[key].value = value
    setInputs(temp)
  }

  const descriptionWithTemplate: InputType = {
    ...inputs.description,
    additionalElements: (
      <button type="button" className="btn-link text-xs" onClick={onSelectTemplate}>
        Select Template
      </button>
    ) as any,
  }

  return (
    <Modal title="Refund Payment" isOpen={true} onOk={onModalOk} onClose={() => onClose()}>
      <>
        <p className="mb-4 mx-4">
          <span className="italic text-red-800">${getPrice2decimal(remainAmount, false, true)}</span> is available of $
          {getPrice2decimal(amount, false, true)}
        </p>
        {Object.keys(inputs).map((key: string) => {
          const input = key === 'description' ? descriptionWithTemplate : inputs[key]
          return (
            <div key={key} className="mb-4">
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Notify</p>
          <EmailTo Data={emailParties} Emails={emails} onChange={(value) => setEmails(value)} />
        </div>
      </>
    </Modal>
  )
}
